import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { oneKlicks } from "../../../SDK/oneclick";
import { useAuth } from "../../../context/AuthContext";

const STATUS_OPTIONS = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
];

const NewCompany = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        company_uid: "",
        name: "",
        legal_name: "",
        email: "",
        phone: "",
        support_email: "",
        support_phone: "",
        website: "",
        address: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        registration_number: "",
        gst_number: "",
        license_number: "",
        password: "",
        confirm_password: "",
        status: "active",
        notes: "",
    });

    useEffect(() => {
        if (!formData.company_uid) {
            setFormData((prev) => ({ ...prev, company_uid: generateCompanyCode() }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const generateCompanyCode = () => {
        const random = Math.random().toString(36).substr(2, 4).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `INS-${random}${timestamp}`;
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!formData.name?.trim()) {
            toast.error("Company name is required");
            return;
        }

        if (!formData.email?.trim()) {
            toast.error("Primary email is required");
            return;
        }

        if (formData.password && formData.password !== formData.confirm_password) {
            toast.error("Passwords do not match");
            return;
        }

        setSubmitting(true);

        try {
            const timestamp = new Date().toISOString().slice(0, 19).replace("T", " ");
            const rawPassword = formData.password?.trim();
            const passwordValue = rawPassword || "PLACEHOLDER";
            const authKeyValue = rawPassword || "PLACEHOLDER";
            const metaPayload = {
                support_email: formData.support_email?.trim() || null,
                support_phone: formData.support_phone?.trim() || null,
                website: formData.website?.trim() || null,
                registration_number: formData.registration_number?.trim() || null,
                gst_number: formData.gst_number?.trim() || null,
                status: formData.status,
                city: formData.city?.trim() || null,
                state: formData.state?.trim() || null,
                country: formData.country?.trim() || null,
                postal_code: formData.postal_code?.trim() || null,
                notes: formData.notes?.trim() || null,
                created_by: userData?.id || null,
            };

            const basePayload = {
                company_uid: formData.company_uid?.trim(),
                name: formData.name?.trim(),
                company_name: formData.name?.trim(),
                legal_name: formData.legal_name?.trim() || null,
                email: formData.email?.trim(),
                company_email: formData.email?.trim(),
                phone: formData.phone?.trim() || null,
                company_phone: formData.phone?.trim() || null,
                status: formData.status,
                password: passwordValue,
                password_hash: passwordValue,
                auth_key: authKeyValue,
                registration_number: formData.registration_number?.trim() || `REG-${Date.now()}`,
                gst_number: formData.gst_number?.trim() || `GST-${Date.now()}`,
                license_number: formData.license_number?.trim() || `LIC-${Date.now()}`,
                created_at: timestamp,
                updated_at: timestamp,
            };

            const enrichedPayload = {
                ...basePayload,
                address: formData.address?.trim() || null,
                company_address: formData.address?.trim() || null,
                city: formData.city?.trim() || null,
                company_city: formData.city?.trim() || null,
                state: formData.state?.trim() || null,
                company_state: formData.state?.trim() || null,
                country: formData.country?.trim() || null,
                company_country: formData.country?.trim() || null,
                postal_code: formData.postal_code?.trim() || null,
                company_postal_code: formData.postal_code?.trim() || null,
                license_number: formData.license_number?.trim() || `LIC-${Date.now()}`,
                meta: JSON.stringify(metaPayload),
            };

            console.log("Creating insurance company payload:", enrichedPayload);
            let response = await oneKlicks.create("", "insurance_companies", enrichedPayload);
            console.log("Create company response:", response);

            if (!response?.success) {
                const errorMessage = response?.error || response?.message || "";
                if (typeof errorMessage === "string" && errorMessage.toLowerCase().includes("unknown column")) {
                    console.warn("Retrying company creation with minimal payload due to schema mismatch.");
                    response = await oneKlicks.create("", "insurance_companies", {
                        ...basePayload,
                        address: formData.address?.trim() || null,
                        company_address: formData.address?.trim() || null,
                        city: formData.city?.trim() || null,
                        company_city: formData.city?.trim() || null,
                        state: formData.state?.trim() || null,
                        company_state: formData.state?.trim() || null,
                        country: formData.country?.trim() || null,
                        company_country: formData.country?.trim() || null,
                        postal_code: formData.postal_code?.trim() || null,
                        company_postal_code: formData.postal_code?.trim() || null,
                        license_number: formData.license_number?.trim() || `LIC-${Date.now()}`,
                        meta: JSON.stringify(metaPayload),
                    });
                    console.log("Create company fallback response:", response);
                }
            }

            if (response?.success) {
                toast.success("Company created successfully");
                navigate("/insurance/agents/new");
            } else {
                toast.error(response?.message || response?.error || "Failed to create company");
            }
        } catch (error) {
            console.error("Error creating company:", error);
            toast.error("Failed to create company");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="subbody" style={{ width: "100%", padding: "24px" }}>
            <div className="dc-haslayout dc-dbsectionspace" style={{ maxWidth: "960px", margin: "0 auto" }}>
                <div className="dc-dashboardbox">
                    <div
                        className="dc-dashboardboxtitle"
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                            gap: "12px",
                        }}
                    >
                        <div>
                            <h2>Add Insurance Company</h2>
                            <p style={{ color: "#6c757d", marginTop: "4px" }}>
                                Register a new insurance company with contact and compliance details.
                            </p>
                        </div>
                        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="insurance-company-form"
                                className="btn btn-primary"
                                disabled={submitting}
                            >
                                {submitting ? "Saving..." : "Create Company"}
                            </button>
                        </div>
                    </div>

                    <div className="dc-dashboardboxcontent" style={{ padding: "24px" }}>
                        <form id="insurance-company-form" className="dc-formtheme" onSubmit={handleSubmit}>
                            <div className="row" style={{ rowGap: "24px" }}>
                                <div className="col-12">
                                    <div className="mb-3">
                                        <h5 style={{ marginBottom: "4px" }}>Company Identity</h5>
                                        <p style={{ color: "#6c757d", marginBottom: 0 }}>
                                            Basic details that uniquely identify the insurance provider.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="company_uid">Company Code</label>
                                        <input
                                            type="text"
                                            id="company_uid"
                                            name="company_uid"
                                            className="form-control"
                                            value={formData.company_uid}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="status">Status</label>
                                        <select
                                            id="status"
                                            name="status"
                                            className="form-control"
                                            value={formData.status}
                                            onChange={handleChange}
                                        >
                                            {STATUS_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="name">Company Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            className="form-control"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            placeholder="e.g. Alpha Health Insurance"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="legal_name">Legal Name</label>
                                        <input
                                            type="text"
                                            id="legal_name"
                                            name="legal_name"
                                            className="form-control"
                                            value={formData.legal_name}
                                            onChange={handleChange}
                                            placeholder="Registered legal entity name"
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-3" style={{ marginTop: "8px" }}>
                                        <h5 style={{ marginBottom: "4px" }}>Primary Contacts</h5>
                                        <p style={{ color: "#6c757d", marginBottom: 0 }}>
                                            Used for invoices, escalations, and login credentials.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="email">Primary Email</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            className="form-control"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="phone">Primary Phone</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            className="form-control"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            placeholder="e.g. +91-98765-00000"
                                        />
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="support_email">Support Email</label>
                                        <input
                                            type="email"
                                            id="support_email"
                                            name="support_email"
                                            className="form-control"
                                            value={formData.support_email}
                                            onChange={handleChange}
                                            placeholder="Customer support mailbox"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="support_phone">Support Phone</label>
                                        <input
                                            type="tel"
                                            id="support_phone"
                                            name="support_phone"
                                            className="form-control"
                                            value={formData.support_phone}
                                            onChange={handleChange}
                                            placeholder="Customer care number"
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-3" style={{ marginTop: "8px" }}>
                                        <h5 style={{ marginBottom: "4px" }}>Credentials</h5>
                                        <p style={{ color: "#6c757d", marginBottom: 0 }}>
                                            Optional login credentials for direct company portal access. Password is hashed before saving.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="password">Password</label>
                                        <input
                                            type="password"
                                            id="password"
                                            name="password"
                                            className="form-control"
                                            value={formData.password}
                                            onChange={handleChange}
                                            placeholder="Enter password"
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-6">
                                    <div className="form-group">
                                        <label htmlFor="confirm_password">Confirm Password</label>
                                        <input
                                            type="password"
                                            id="confirm_password"
                                            name="confirm_password"
                                            className="form-control"
                                            value={formData.confirm_password}
                                            onChange={handleChange}
                                            placeholder="Re-enter password"
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-3" style={{ marginTop: "8px" }}>
                                        <h5 style={{ marginBottom: "4px" }}>Compliance & Licensing</h5>
                                        <p style={{ color: "#6c757d", marginBottom: 0 }}>
                                            Regulatory identifiers required for onboarding. Please double-check these values.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-12 col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="registration_number">Registration Number</label>
                                        <input
                                            type="text"
                                            id="registration_number"
                                            name="registration_number"
                                            className="form-control"
                                            value={formData.registration_number}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="gst_number">GST / Tax Number</label>
                                        <input
                                            type="text"
                                            id="gst_number"
                                            name="gst_number"
                                            className="form-control"
                                            value={formData.gst_number}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-4">
                                    <div className="form-group">
                                        <label htmlFor="license_number">Insurance License Number</label>
                                        <input
                                            type="text"
                                            id="license_number"
                                            name="license_number"
                                            className="form-control"
                                            value={formData.license_number}
                                            onChange={handleChange}
                                            required
                                            placeholder="Regulatory license ID"
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-3" style={{ marginTop: "8px" }}>
                                        <h5 style={{ marginBottom: "4px" }}>Address & Website</h5>
                                        <p style={{ color: "#6c757d", marginBottom: 0 }}>
                                            Location and digital touchpoints for the insurer.
                                        </p>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="address">Head Office Address</label>
                                        <textarea
                                            id="address"
                                            name="address"
                                            className="form-control"
                                            rows="2"
                                            value={formData.address}
                                            onChange={handleChange}
                                            placeholder="Street, locality"
                                        ></textarea>
                                    </div>
                                </div>

                                <div className="col-12 col-md-3">
                                    <div className="form-group">
                                        <label htmlFor="city">City</label>
                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            className="form-control"
                                            value={formData.city}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-3">
                                    <div className="form-group">
                                        <label htmlFor="state">State</label>
                                        <input
                                            type="text"
                                            id="state"
                                            name="state"
                                            className="form-control"
                                            value={formData.state}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-3">
                                    <div className="form-group">
                                        <label htmlFor="country">Country</label>
                                        <input
                                            type="text"
                                            id="country"
                                            name="country"
                                            className="form-control"
                                            value={formData.country}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>
                                <div className="col-12 col-md-3">
                                    <div className="form-group">
                                        <label htmlFor="postal_code">Postal Code</label>
                                        <input
                                            type="text"
                                            id="postal_code"
                                            name="postal_code"
                                            className="form-control"
                                            value={formData.postal_code}
                                            onChange={handleChange}
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="website">Website</label>
                                        <input
                                            type="url"
                                            id="website"
                                            name="website"
                                            className="form-control"
                                            value={formData.website}
                                            onChange={handleChange}
                                            placeholder="https://"
                                        />
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="form-group">
                                        <label htmlFor="notes">Notes</label>
                                        <textarea
                                            id="notes"
                                            name="notes"
                                            className="form-control"
                                            rows="3"
                                            value={formData.notes}
                                            onChange={handleChange}
                                            placeholder="Internal remarks, agreements, support SLA etc."
                                        ></textarea>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewCompany;
